import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { TagIcon, GlobeIcon, LayoutTemplateIcon, SparklesIcon, FlameIcon, CrownIcon, ClockIcon } from 'lucide-react';
export function UrgencySection() {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  useEffect(() => {
    // Rolling short countdown — resets every 24h to maintain urgency
    const getTarget = () => {
      const now = new Date();
      const endOfDay = new Date(now);
      endOfDay.setHours(23, 59, 59, 999);
      return endOfDay.getTime();
    };
    const update = () => {
      const diff = getTarget() - Date.now();
      if (diff > 0) {
        setTimeLeft({
          days: 0,
          hours: Math.floor(diff / (1000 * 60 * 60)),
          minutes: Math.floor(diff % (1000 * 60 * 60) / (1000 * 60)),
          seconds: Math.floor(diff % (1000 * 60) / 1000)
        });
      }
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return <section id="pricing" className="py-10 md:py-20 relative overflow-hidden" data-id="element-579">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-brand-darkest via-brand-dark to-[#00283a]" data-id="element-580"></div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-581">
        {/* Header */}
        <div className="text-center mb-8 md:mb-10" data-id="element-582">
          <div className="inline-block bg-red-500 text-white font-bold text-xs px-3 py-1 rounded-full mb-5 animate-pulse" data-id="element-583">
            SELLING FAST
          </div>

          <h2 className="text-3xl md:text-5xl font-heading font-black mb-3" data-id="element-584">
            Limited <span className="text-gradient" data-id="element-585">Bundle Offers</span>
          </h2>
          <p className="text-base md:text-lg text-gray-300 mb-2 max-w-xl mx-auto" data-id="element-586">
            Buy more, save more. Gear up your whole crew.
          </p>
          <p className="text-xs text-gray-500 max-w-md mx-auto" data-id="element-587">
            Only 5,000 units in the first drop. Once they're gone, they're gone.
          </p>
        </div>

        {/* Compact Countdown Strip */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4
      }} className="flex items-center justify-center gap-2 mb-10 md:mb-14" data-id="element-588">
          <div className="inline-flex items-center gap-3 px-4 py-2.5 rounded-full" style={{
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(0,102,255,0.2)'
        }} data-id="element-589">
            <div className="flex items-center gap-1.5" data-id="element-590">
              <span className="relative flex h-2 w-2" data-id="element-591">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" data-id="element-592"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" data-id="element-593"></span>
              </span>
              <ClockIcon className="w-3.5 h-3.5 text-brand-glow" data-id="element-594" />
              <span className="text-[10px] sm:text-xs font-bold text-red-400/80 uppercase tracking-wider" data-id="element-595">
                Today's offer ends in
              </span>
            </div>
            <div className="flex items-center gap-1.5 font-heading font-black text-sm sm:text-base text-white" data-id="element-596">
              <span className="text-red-400" data-id="element-597">
                {pad(timeLeft.hours)}
                <span className="text-red-400/60 text-[10px] ml-0.5" data-id="element-598">h</span>
              </span>
              <span className="text-red-400/40" data-id="element-599">:</span>
              <span className="text-red-400" data-id="element-600">
                {pad(timeLeft.minutes)}
                <span className="text-red-400/60 text-[10px] ml-0.5" data-id="element-601">m</span>
              </span>
              <span className="text-red-400/40" data-id="element-602">:</span>
              <span className="text-red-400" data-id="element-603">
                {pad(timeLeft.seconds)}
                <span className="text-red-400/60 text-[10px] ml-0.5" data-id="element-604">s</span>
              </span>
            </div>
          </div>
        </motion.div>

        {/* Bundle Pricing Tiers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mb-10 md:mb-14" data-id="element-605">
          {/* Tier 1: Single */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="relative rounded-2xl p-5 md:p-6 text-left" style={{
          background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)',
          border: '1px solid rgba(255,255,255,0.08)'
        }} data-id="element-606">
            <div className="flex items-center gap-2 mb-3" data-id="element-607">
              <SparklesIcon className="w-4 h-4 text-gray-400" data-id="element-608" />
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-609">
                Starter
              </span>
            </div>
            <div className="mb-1 flex items-baseline gap-2" data-id="element-610">
              <div data-id="element-611">
                <span className="text-3xl md:text-4xl font-heading font-black text-white" data-id="element-612">
                  $69
                </span>
                <span className="text-base text-gray-400 font-heading" data-id="element-613">
                  .95
                </span>
              </div>
            </div>
            <p className="text-[11px] text-gray-500 mb-4" data-id="element-614">per t-shirt</p>
            <div className="space-y-2 text-sm" data-id="element-615">
              <div className="flex items-center gap-2 text-gray-300" data-id="element-616">
                <div className="w-1.5 h-1.5 rounded-full bg-gray-500" data-id="element-617"></div>1
                Smart T-Shirt
              </div>
              <div className="flex items-center gap-2 text-gray-300" data-id="element-618">
                <div className="w-1.5 h-1.5 rounded-full bg-gray-500" data-id="element-619"></div>
                Share the experience with others
              </div>
            </div>
            <div className="mt-5 pt-4 border-t border-white/5" data-id="element-620">
              <p className="text-xs text-gray-500 mb-4" data-id="element-621">
                Total: <span className="text-white font-bold" data-id="element-622">$69.95</span>
              </p>
              <a href="https://shop.qtap.me/collections/world-cup-2026-smart-t-shirt" target="_blank" rel="noopener noreferrer" className="block w-full text-center py-2.5 rounded-lg border border-white/15 text-sm font-bold text-gray-300 hover:bg-white/5 transition-all" data-id="element-623">
                Get Yours
              </a>
            </div>
          </motion.div>

          {/* Tier 2: 3 Pack — MOST POPULAR */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="relative rounded-2xl p-5 md:p-6 text-left md:-my-2 md:py-8" style={{
          background: 'linear-gradient(180deg, rgba(0,102,255,0.12) 0%, rgba(0,102,255,0.03) 100%)',
          border: '1.5px solid rgba(0,102,255,0.35)',
          boxShadow: '0 0 50px rgba(0,102,255,0.12), 0 0 100px rgba(0,102,255,0.05)'
        }} data-id="element-624">
            {/* Badge */}
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-cyan text-brand-darkest font-black text-[10px] px-4 py-1 rounded-full uppercase tracking-wider whitespace-nowrap" data-id="element-625">
              🔥 Most Popular
            </div>
            <div className="flex items-center gap-2 mb-3" data-id="element-626">
              <FlameIcon className="w-4 h-4 text-brand-cyan" data-id="element-627" />
              <span className="text-xs font-bold text-brand-cyan uppercase tracking-wider" data-id="element-628">
                3 Pack
              </span>
              <span className="ml-auto bg-brand-cyan/15 text-brand-cyan text-[10px] font-bold px-2 py-0.5 rounded-full" data-id="element-629">
                -15%
              </span>
            </div>
            <div className="mb-1 flex items-baseline gap-2" data-id="element-630">
              <span className="text-3xl md:text-4xl font-heading font-black text-white" data-id="element-631">
                $59
              </span>
              <span className="text-base text-gray-400 font-heading" data-id="element-632">.46</span>
              <span className="text-xs text-gray-500 line-through ml-1" data-id="element-633">
                $69.95
              </span>
            </div>
            <p className="text-[11px] text-brand-cyan font-bold mb-4" data-id="element-634">
              per t-shirt — Save 15%
            </p>
            <div className="space-y-2 text-sm" data-id="element-635">
              <div className="flex items-center gap-2 text-gray-200" data-id="element-636">
                <div className="w-1.5 h-1.5 rounded-full bg-brand-cyan" data-id="element-637"></div>3
                Smart T-Shirts
              </div>
              <div className="flex items-center gap-2 text-gray-200" data-id="element-638">
                <div className="w-1.5 h-1.5 rounded-full bg-brand-cyan" data-id="element-639"></div>
                Perfect for Friends
              </div>
              <div className="flex items-center gap-2 text-brand-cyan font-medium" data-id="element-640">
                <div className="w-1.5 h-1.5 rounded-full bg-brand-cyan" data-id="element-641"></div>
                You save $31.47
              </div>
            </div>
            <div className="mt-5 pt-4 border-t border-brand-cyan/20" data-id="element-642">
              <p className="text-xs text-gray-400 mb-4" data-id="element-643">
                Total: <span className="text-white font-bold" data-id="element-644">$178.38</span>
                <span className="text-gray-500 line-through ml-2" data-id="element-645">$209.85</span>
              </p>
              <a href="https://shop.qtap.me/collections/world-cup-2026-smart-t-shirt" target="_blank" rel="noopener noreferrer" className="block w-full text-center py-3 rounded-lg bg-brand-cyan hover:bg-brand-sky text-brand-darkest text-sm font-black transition-all hover:shadow-[0_0_20px_rgba(0,102,255,0.4)]" data-id="element-646">
                Get the Bundle
              </a>
            </div>
          </motion.div>

          {/* Tier 3: 5 Pack — BEST VALUE */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="relative rounded-2xl p-5 md:p-6 text-left" style={{
          background: 'linear-gradient(180deg, rgba(250,204,21,0.07) 0%, rgba(250,204,21,0.01) 100%)',
          border: '1px solid rgba(250,204,21,0.25)'
        }} data-id="element-647">
            {/* Badge */}
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-yellow-400 text-brand-darkest font-black text-[10px] px-4 py-1 rounded-full uppercase tracking-wider whitespace-nowrap" data-id="element-648">
              👑 Best Value
            </div>
            <div className="flex items-center gap-2 mb-3" data-id="element-649">
              <CrownIcon className="w-4 h-4 text-yellow-400" data-id="element-650" />
              <span className="text-xs font-bold text-yellow-400 uppercase tracking-wider" data-id="element-651">
                5 Pack
              </span>
              <span className="ml-auto bg-yellow-400/15 text-yellow-400 text-[10px] font-bold px-2 py-0.5 rounded-full" data-id="element-652">
                -25%
              </span>
            </div>
            <div className="mb-1 flex items-baseline gap-2" data-id="element-653">
              <span className="text-3xl md:text-4xl font-heading font-black text-white" data-id="element-654">
                $52
              </span>
              <span className="text-base text-gray-400 font-heading" data-id="element-655">.46</span>
              <span className="text-xs text-gray-500 line-through ml-1" data-id="element-656">
                $69.95
              </span>
            </div>
            <p className="text-[11px] text-yellow-400 font-bold mb-4" data-id="element-657">
              per t-shirt — Save 25%
            </p>
            <div className="space-y-2 text-sm" data-id="element-658">
              <div className="flex items-center gap-2 text-gray-200" data-id="element-659">
                <div className="w-1.5 h-1.5 rounded-full bg-yellow-400" data-id="element-660"></div>5
                Smart T-Shirts
              </div>
              <div className="flex items-center gap-2 text-gray-200" data-id="element-661">
                <div className="w-1.5 h-1.5 rounded-full bg-yellow-400" data-id="element-662"></div>
                Built for your Squad
              </div>
              <div className="flex items-center gap-2 text-yellow-400 font-medium" data-id="element-663">
                <div className="w-1.5 h-1.5 rounded-full bg-yellow-400" data-id="element-664"></div>
                You save $87.45
              </div>
            </div>
            <div className="mt-5 pt-4 border-t border-yellow-400/20" data-id="element-665">
              <p className="text-xs text-gray-400 mb-4" data-id="element-666">
                Total: <span className="text-white font-bold" data-id="element-667">$262.30</span>
                <span className="text-gray-500 line-through ml-2" data-id="element-668">$349.75</span>
              </p>
              <a href="https://shop.qtap.me/collections/world-cup-2026-smart-t-shirt" target="_blank" rel="noopener noreferrer" className="block w-full text-center py-2.5 rounded-lg border border-yellow-400/30 text-sm font-bold text-yellow-400 hover:bg-yellow-400/10 transition-all" data-id="element-669">
                Get the Best Deal
              </a>
            </div>
          </motion.div>
        </div>

        {/* Perks Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-10 md:mb-14" data-id="element-670">
          <div className="bg-white/[0.03] border border-white/5 p-3.5 rounded-xl flex items-center gap-3" data-id="element-671">
            <TagIcon className="w-4 h-4 text-brand-cyan shrink-0" data-id="element-672" />
            <span className="font-medium text-xs text-gray-300" data-id="element-673">
              Big Discounts
            </span>
          </div>
          <div className="bg-white/[0.03] border border-white/5 p-3.5 rounded-xl flex items-center gap-3" data-id="element-674">
            <GlobeIcon className="w-4 h-4 text-brand-cyan shrink-0" data-id="element-675" />
            <span className="font-medium text-xs text-gray-300" data-id="element-676">
              Worldwide Shipping
            </span>
          </div>
          <div className="bg-white/[0.03] border border-white/5 p-3.5 rounded-xl flex items-center gap-3" data-id="element-677">
            <LayoutTemplateIcon className="w-4 h-4 text-brand-cyan shrink-0" data-id="element-678" />
            <span className="font-medium text-xs text-gray-300" data-id="element-679">
              Limited Drop
            </span>
          </div>
        </div>

        {/* Footer CTA */}
        <div className="text-center" data-id="element-680">
          <p className="text-gray-500 text-xs" data-id="element-681">
            Join 2,000+ people already on the waitlist
          </p>
        </div>
      </div>
    </section>;
}